import algoliasearch from 'algoliasearch/lite';
import get from 'lodash/get';

import isPrerender from 'Helpers/SEO/is-prerender';
import isBot from 'Helpers/SEO/is-bot';
import { get as apiGet } from 'Api';
import store from 'Redux/store';
import env from 'Env';
import stringToHash from 'Helpers/string-to-hash';

export async function getSearchKey(mustFetchAgain = false) {
  const thisDate = Date.now();
  const fetchAgain = (
    mustFetchAgain ||
    !window.searchKey ||
    !window.searchKeyExp ||
    thisDate >= Number(window.searchKeyExp)
  );
  if (fetchAgain) {
    const { response, data } = await apiGet(store.dispatch, '/algolia/key');

    if (response && response.ok) {
      window.searchKey = get(data, 'key', '');
      window.searchKeyExp = get(data, 'validUntil', 0);
    } else {
      window.location.reload();
      return null;
    }
  }
  return window.searchKey;
}

let requests = [];

const hasThrown = {};

/**
 * Special error class for rate limiting, to help sentry grouping
 * @type Error
 */
export class AlgoliaRateLimitError extends Error {
  constructor(message, hash) {
    super(message);
    this.hash = hash;

    if (hasThrown[hash]) {
      this.ignore = true;
    } else {
      hasThrown[hash] = true;
    }
  }
}

/**
 * Check if request will go over the limit then log request
 * @param  {String} path Hash of algolia query
 * @throws {Error}
 */
const rateLimiter = (hash) => {
  const timestamp = Date.now();
  const threshold = timestamp - (env.ratelimit.algoliaInterval * 1000);

  requests = requests.filter(r => r.timestamp > threshold);

  const hashRequests = requests.filter(r => r.hash === hash);

  if (hashRequests.length > env.ratelimit.algoliaLimit) {
    throw new AlgoliaRateLimitError('Algolia self rate limit exceeded', hash);
  }

  requests.push({ timestamp, hash });
};

const errorCatch = async (ex, isRetry, retryFunc) => {
  if (ex && (ex.message === 'Invalid Application-ID or API key' || ex?.message.includes('expired'))) {
    if (!isRetry) {
      return retryFunc();
    }

    // Next error, reload with url flag to stop multiple reloads
    const url = window.location.href;
    if (!url.includes('algoliaKeyExpired=1')) {
      window.location.href = `${url}${url.includes('?') ? '&' : '?'}algoliaKeyExpired=1`;

      return false;
    }
  }

  if (ex instanceof Error) {
    return Promise.reject(ex);
  }

  return Promise.reject(new Error(ex.message, { cause: ex }));
};

export async function getClient(isRetry = false, isInstantSearch = false) {
  const searchKey = await getSearchKey(isRetry);
  const client = algoliasearch('W2M9492DDV', searchKey);

  // Clone method before overwriting
  client._search = client.search;

  client.search = (...params) => {
    if (Array.isArray(params[0])) {
      params[0].forEach((query) => {
        query.params.analyticsTags = ['frontend', ...(query.params.analyticsTags || [])];
      });

      // Instant search seems to include a lot of queries where caching already occurs
      if (!isInstantSearch) {
        const hash = stringToHash(JSON.stringify({ params }));

        rateLimiter(hash);
      }
    }

    return client._search(...params)
      .catch(async ex => errorCatch(ex, isRetry, async () => {
        const freshClient = await getClient(true, isInstantSearch);
        return freshClient.search(...params);
      }));
  };

  return client;
}

export async function getIndex(indexName, isRetry = false) {
  const searchKey = await getSearchKey(isRetry);
  const client = algoliasearch('W2M9492DDV', searchKey);

  if (!indexName) return client;

  const index = client.initIndex(indexName);

  // Clone method before overwriting
  index._search = index.search;

  // Wrapper for search with API key expiry error catch and retry
  index.search = (_query, params, ...rest) => {
    if (typeof params.page !== 'undefined') {
      params.page = Number(params.page) || 0;
    }

    if (typeof params.hitsPerPage !== 'undefined') {
      params.hitsPerPage = Number(params.hitsPerPage) || 36;
    }

    let query = _query;

    if (query && query.length > 512) {
      query = query.substring(0, 512);
    }

    params.analyticsTags = ['frontend', ...(params.analyticsTags || [])];

    if (isPrerender()) {
      params.analyticsTags.push('isPrerender');
    } else if (isBot()) {
      params.analyticsTags.push('isBot');
    }

    const hash = stringToHash(JSON.stringify({ query, params }));

    rateLimiter(hash);

    return index._search(query, params, ...rest)
      .catch(async ex => errorCatch(ex, isRetry, async () => {
        const freshIndex = await getIndex(indexName, true);
        return freshIndex.search(query, params, ...rest);
      }));
  };

  return index;
}
